import {
  type Icon,
  type Link,
  type Status,
  type DatasourceDataResponse,
  type DatasourceParameters
} from '@atlaskit/linking-types';
import {
  type CloudId,
  ContentRepresentation,
  type JiraColumn,
  type JiraIssueRef,
  type JiraServerId,
  type ResolvedJiraIssueRef,
  type Authorization,
  type ErrorResult
} from '@databases/types/confluence';
import { type Cheerio } from 'cheerio';
import { groupBy, uniq, uniqBy } from 'lodash-es';
import { errorConstructors } from 'serialize-error';

import { cheerioIterator, parseHTML } from '../lib/dom.ts';
import { serializeMacro } from '../macro.ts';
import { getThrottledBatchCallback } from '../utils.ts';

import { convertContentBody } from './content.ts';
import {
  getDatasourceResponseByType,
  DATASOURCE_DEFAULT_PAGE_SIZE,
  DATASOURCE_FETCH_ALL_PAGE_SIZE,
  DATASOURCE_MAX_TOTAL_COUNT
} from './datasource.ts';
import { getTextFromDatasourceValue, quoteCql } from './utils.ts';

export const requiredJiraColumns: JiraColumn[] = ['key', 'summary', 'status', 'type'];
export const requiredJiraDatasourceColumns: JiraColumn[] = [
  'key',
  'summary',
  'status',
  'type',
  'issuetype',
  'id',
  'ari',
  'projectId'
];

export type ResolvedJiraIssuesPage = {
  nextPageCursor: string | undefined;
  issues: ResolvedJiraIssueRef[];
  totalCount?: number;
};

const CLOUD_ID_KEY = 'cloudId:';
const SERVER_ID_KEY = 'serverId:';

/**
 * If the passed string looks like a Jira issue key (in the form ABC-123) or a link to a Jira issue,
 * returns that issue key. Otherwise returns undefined.
 */
export function matchJiraIssueKey(str: string): string | undefined {
  let m = str.match(/\/browse\/([A-Z0-9]+-[0-9]+)(\?|$)/);
  if (m) {
    return m[1];
  }

  m = str.match(/^[A-Z0-9]+-[0-9]+$/);
  if (m) {
    return m[0];
  }
}

/**
 * When this error is thrown, it means that the current user has not authorized access to their user account on the target
 * Jira instance yet, and needs to do so by opening the URL in the authenticationPath property.
 */
export class JiraAuthenticationError extends Error {
  name = 'JiraAuthenticationError';
  authenticationPath?: string;
}
errorConstructors.set(
  'JiraAuthenticationError',
  JiraAuthenticationError as ErrorConstructor
);

/**
 * Checks whether there is an authentication link in the given export format HTML, and if there is, throws an {@link JiraAuthenticationError}.
 */
function checkForAuthentication(html: Cheerio<any>): void {
  const authLink = html.find('a.static-oauth-init');
  if (authLink.length > 0) {
    // Note that the auth link has the wrong base URL, so we discard it here and only persist the path
    const authPathMatch = authLink.attr('href')?.match(/\/wiki(\/.*$)/);
    throw Object.assign(new JiraAuthenticationError('Not authenticated with Jira.'), {
      authenticationPath: authPathMatch?.[1]
    });
  }
}

/**
 * Checks whether there is an error in the given export format HTML.
 */
function containsExportError(html: Cheerio<any>): boolean {
  return html.find('.jim-error-message').length > 0;
}

/**
 * Takes a single multi-issue Jira macro in export format and extracts the individual issues and their fields from it.
 * @param columns The Jira fields that the macro is displaying, in order.
 * @throws Throws a {@link JiraAuthenticationError} if authentication is required
 */
function parseJiraMacro(
  serverId: JiraServerId,
  macroExportView: string,
  columns: JiraColumn[]
): Authorization<ResolvedJiraIssueRef[]> {
  const html = parseHTML(macroExportView);

  if (containsExportError(html)) {
    return {
      authorized: false,
      access: 'unauthorized'
    };
  }

  checkForAuthentication(html);

  const data = [
    ...cheerioIterator(
      html.find(
        'div.jira-issues > table > tbody > tr.rowNormal,div.jira-issues > table > tbody > tr.rowAlternate'
      )
    )
  ].map((tr): ResolvedJiraIssueRef => {
    const tds = tr.children('td');
    const resolvedColumns = Object.fromEntries(columns.map((col, i) => [col, tds.eq(i)]));
    const issueLink = resolvedColumns['key'].find('a[href]');
    const status = resolvedColumns['status'].find('.aui-lozenge');
    const typeImg = resolvedColumns['type'].find('img');
    return {
      serverId,
      key: issueLink.text(),
      url: issueLink.attr('href') ?? '',
      summary: resolvedColumns['summary'].text().trim(),
      statusCategory: status.hasClass('aui-lozenge-success')
        ? 'done'
        : status.hasClass('aui-lozenge-current')
          ? 'in-progress'
          : 'to-do',
      statusName: status.text().trim(),
      typeIconUrl: typeImg.attr('src') ?? '',
      type: typeImg.attr('alt') ?? '',
      columns: Object.fromEntries(
        Object.entries(resolvedColumns).map(([col, el]) => [
          col,
          {
            html: el.html()?.trim() ?? '',
            text: el.text().trim(),
            type: 'string'
          }
        ])
      )
    };
  });

  return { authorized: true, access: 'granted', data };
}

export async function getJiraIssuesByJql(
  serverId: JiraServerId,
  jql: string,
  columns: JiraColumn[] = [],
  limit = 20
): Promise<Authorization<ResolvedJiraIssueRef[]>> {
  const actualColumns = uniq([...requiredJiraColumns, ...columns]);
  const converted = await convertContentBody(
    {
      value: serializeMacro('jira', {
        columns: actualColumns.join(','),
        maximumIssues: limit,
        jqlQuery: jql,
        serverId
      }),
      representation: ContentRepresentation.STORAGE
    },
    { to: ContentRepresentation.EXPORT_VIEW }
  );
  return parseJiraMacro(serverId, converted.value, actualColumns);
}

export function mapAppearanceToStatusCategory(
  appearance: string | undefined
): 'to-do' | 'in-progress' | 'done' {
  switch (appearance) {
    case 'success':
    case 'removed':
      return 'done';
    case 'inprogress':
    case 'moved':
      return 'in-progress';
    case 'new':
    case 'default':
    default:
      return 'to-do';
  }
}

function parseDatasourceResponse(
  cloudId: CloudId,
  response: DatasourceDataResponse
): Authorization<ResolvedJiraIssueRef[]> {
  const {
    data: { items, schema },
    meta: { access }
  } = response;
  const columns = schema?.properties ?? [];

  if (access !== 'granted') {
    return { authorized: false, access };
  }

  const data = items.map((item): ResolvedJiraIssueRef => {
    const { issuetype, key, status, summary } = item;
    const statusData = status.data as Status;
    const statusName = statusData.text ?? '';
    const statusCategory = mapAppearanceToStatusCategory(statusData.style?.appearance);

    return {
      cloudId,
      key: (key.data as Link).text ?? '',
      url: (key.data as Link).url ?? '',
      summary: (summary.data as Link).text ?? '',
      statusCategory,
      statusName,
      typeIconUrl: (issuetype.data as Icon).source ?? '',
      type: (issuetype.data as Icon).label ?? '',
      // Extract the new fields - they follow the same {data: string} pattern
      id: (item.id?.data as string) ?? '',
      ari: (item.ari?.data as string) ?? '',
      projectId: (item.projectId?.data as string) ?? '',
      columns: Object.fromEntries(
        columns.map((column) => {
          const text = getTextFromDatasourceValue(column, item);
          const result = {
            html: text,
            type: column.type,
            text
          };
          const rawData = item?.[column.key]?.data;
          if (rawData != null) {
            return [
              column.key,
              {
                ...result,
                data: rawData
              }
            ];
          }
          return [column.key, result];
        })
      )
    };
  });

  return { authorized: true, access, data };
}

export async function getJiraIssuesFromDatasourceByJql(
  cloudId: CloudId,
  parameters: DatasourceParameters,
  columns: JiraColumn[] = [],
  pageSize = DATASOURCE_DEFAULT_PAGE_SIZE,
  pageCursor = ''
): Promise<Authorization<ResolvedJiraIssuesPage>> {
  const fields = uniq([...requiredJiraDatasourceColumns, ...columns]);

  const datasourceResponse = await getDatasourceResponseByType(
    'jira',
    fields,
    parameters,
    pageSize,
    pageCursor
  );

  // TODO: https://product-fabric.atlassian.net/browse/NCTD-3072
  if (!datasourceResponse || !datasourceResponse.data) {
    return { authorized: false, access: 'forbidden' };
  }

  const parsedResponse = parseDatasourceResponse(cloudId, datasourceResponse);

  if (parsedResponse.authorized) {
    return {
      ...parsedResponse,
      data: {
        nextPageCursor: datasourceResponse.data.nextPageCursor,
        issues: parsedResponse.data,
        totalCount: datasourceResponse.data.totalCount
      }
    };
  }

  return parsedResponse;
}

async function getAllJiraIssuesFromDatasourceByJql(
  cloudId: CloudId,
  jql: string,
  columns: JiraColumn[] = [],
  pageSize = DATASOURCE_FETCH_ALL_PAGE_SIZE
): Promise<Authorization<ResolvedJiraIssueRef[]>> {
  let currentPageCursor: string | undefined = '';
  let allIssues: ResolvedJiraIssueRef[] = [];
  const response = await getJiraIssuesFromDatasourceByJql(
    cloudId,
    { cloudId, jql },
    columns,
    pageSize
  );

  if (!response.authorized) {
    return response;
  }

  currentPageCursor = response.data.nextPageCursor;
  allIssues = allIssues.concat(response.data.issues);

  while (currentPageCursor) {
    const response = await getJiraIssuesFromDatasourceByJql(
      cloudId,
      { cloudId, jql },
      columns,
      pageSize,
      currentPageCursor
    );

    if (response.authorized) {
      currentPageCursor = response.data.nextPageCursor;
      allIssues = allIssues.concat(response.data.issues);
    }
  }

  return { authorized: true, access: 'granted', data: allIssues };
}

export async function getJiraIssuesByQuery(
  cloudId: CloudId,
  serverId: JiraServerId,
  query: string,
  columns: JiraColumn[] = []
): Promise<ResolvedJiraIssueRef[]> {
  const jql = `key = ${quoteCql(
    matchJiraIssueKey(query.trim()) ?? query
  )} OR summary ~ ${quoteCql(query)} OR description ~ ${quoteCql(query)}`;

  let issues: ResolvedJiraIssueRef[] = [];
  let totalCount = 0;
  let nextPageCursor: string | undefined = undefined;

  if (cloudId) {
    do {
      const response = await getJiraIssuesFromDatasourceByJql(
        cloudId,
        { cloudId, jql },
        columns,
        DATASOURCE_FETCH_ALL_PAGE_SIZE,
        nextPageCursor ?? ''
      );

      if (!response.authorized) {
        return issues;
      }

      issues = issues.concat(response.data.issues);
      totalCount = Math.min(response.data.totalCount ?? 0, DATASOURCE_MAX_TOTAL_COUNT);
      nextPageCursor = response.data.nextPageCursor;
    } while (issues.length < totalCount && nextPageCursor);
  }

  if (issues.length > 0) {
    return issues;
  }

  const response = await getJiraIssuesByJql(serverId, jql, columns);
  return response.authorized ? response.data : [];
}

export async function getRecentJiraIssues(
  cloudId: CloudId,
  serverId: JiraServerId,
  columns: JiraColumn[] = []
): Promise<ResolvedJiraIssueRef[]> {
  const jql = `issue in issueHistory()`;
  if (cloudId) {
    const response = await getJiraIssuesFromDatasourceByJql(
      cloudId,
      { cloudId, jql },
      columns
    );

    return response.authorized ? response.data.issues : [];
  }
  const authorization = await getJiraIssuesByJql(serverId, jql, columns);
  return authorization.authorized ? authorization.data : [];
}

async function fetchJiraIssuesFromDatasource(
  cloudId: CloudId,
  refs: JiraIssueRef[],
  columns: JiraColumn[] = []
): Promise<Authorization<ResolvedJiraIssueRef[]>> {
  return await getAllJiraIssuesFromDatasourceByJql(
    cloudId,
    `key in (${refs.map((ref) => quoteCql(ref.key)).join(',')})`,
    columns
  );
}

async function fetchJiraIssues(
  serverId: JiraServerId,
  refs: JiraIssueRef[],
  columns: JiraColumn[] = []
): Promise<Authorization<ResolvedJiraIssueRef[]>> {
  return await getJiraIssuesByJql(
    serverId,
    `key in (${refs.map((ref) => quoteCql(ref.key)).join(',')})`,
    columns
  );
}

async function resolveJiraIssuesByIdType(
  id: string,
  refGroup: JiraIssueRef[],
  columns: JiraColumn[]
) {
  if (id.startsWith(CLOUD_ID_KEY)) {
    const cloudId = id.replace(CLOUD_ID_KEY, '');

    return await fetchJiraIssuesFromDatasource(cloudId, refGroup, columns);
  }

  const serverId = id.replace(SERVER_ID_KEY, '');
  return await fetchJiraIssues(serverId, refGroup, columns);
}

export function groupRefsById(refs: JiraIssueRef[]): Record<string, JiraIssueRef[]> {
  const groups = groupBy(refs, (ref) =>
    ref.cloudId
      ? `${CLOUD_ID_KEY}${ref.cloudId}`
      : ref.serverId
        ? `${SERVER_ID_KEY}${ref.serverId}`
        : ''
  );

  return groups;
}

/**
 * Resolves Jira issues by their keys and columns. This will make multiple requests for each unique serverId or cloudId
 * and awaits all of them before returning the results.
 */
async function resolveJiraIssuesByDatasource(
  args: Array<[ref: JiraIssueRef, columns: JiraColumn[]]>
): Promise<Authorization<ResolvedJiraIssueRef[]>[]> {
  const refs = uniqBy(
    args.map((arg) => arg[0]),
    (ref) => `${ref.cloudId ?? ''}:${ref.serverId ?? ''}:${ref.key}`
  );

  const columns = uniq(args.flatMap((arg) => arg[1]));
  const groupedRefs = groupRefsById(refs);
  const promises: Promise<Authorization<ResolvedJiraIssueRef[]>>[] = [];

  for (const [serverIdOrCloudId, refGroup] of Object.entries(groupedRefs)) {
    promises.push(resolveJiraIssuesByIdType(serverIdOrCloudId, refGroup, columns));
  }

  return await Promise.all(promises);
}

export const resolveJiraIssue = getThrottledBatchCallback<
  [ref: JiraIssueRef, columns: JiraColumn[]],
  Authorization<ResolvedJiraIssueRef[]>[]
>(async (args) => {
  return [await resolveJiraIssuesByDatasource(args)];
}, 300);

export async function getJiraIssue(baseUrl: URL, issueKey: string) {
  const response = await fetch(`${baseUrl}/rest/api/3/issue/${issueKey}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });

  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response?.json();

    const err: ErrorResult = {
      status: response.status,
      message: errorBody.errorMessages
    };
    throw err;
  }
}
